import { remoteDbs, type DbEnv } from "../constants/db";
import type { Server, ServerEnvironment } from "../constants/env";
import { ENVIRONMENTS, isServer } from "../constants/env";
import Flags from "../constants/flags";

/**
 * This is for use in the client.
 * It will return the current environment based PUBLIC_* environment variables.
 * @returns ServerEnvironment
 */
export function getClientEnvironment(): ServerEnvironment {
  const isLocalhost =
    (!isServer && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1")) ||
    (isServer && !remoteDbs.includes(Flags.DB_ENV));

  const cognitoEnv = Flags.COGNITO_ENV || ENVIRONMENTS.develop;
  const dbEnv = Flags.DB_ENV || ENVIRONMENTS.local;
  const gitTag = Flags.GIT_TAG || "N/A";

  return {
    server: isLocalhost ? ENVIRONMENTS.local : ENVIRONMENTS[dbEnv as Server],
    localhost: isLocalhost,
    db: dbEnv,
    cognito: cognitoEnv,
    branch: Flags.GIT_BRANCH,
    tag: gitTag,
    commit: Flags.GIT_COMMIT_HASH,
    developmentMode: Flags.IS_DEV_MODE,
  };
}

/**
 * This is for use on the server.
 * It will return the current server configuration based on process.env.* variables.
 * @returns ServerEnvironment
 */
export function getServerEnvironment(): ServerEnvironment {
  const dbEnv = (process.env.DB_ENV ?? ENVIRONMENTS.local) as DbEnv;
  const cognitoEnv = (process.env.COGNITO_ENV ?? ENVIRONMENTS.develop) as Server;
  const isLocal = !remoteDbs.includes(dbEnv);

  return {
    server: isLocal ? ENVIRONMENTS.local : ENVIRONMENTS[dbEnv as Server],
    localhost: !isServer,
    db: dbEnv,
    cognito: cognitoEnv,
    branch: process.env.GIT_BRANCH ?? "",
    tag: process.env.GIT_TAG ?? "",
    commit: process.env.GIT_COMMIT_HASH ?? "",
    developmentMode: process.env.NODE_ENV !== "production",
  };
}

/**
 * This is for shared tooling, when you aren't sure which env you're in.
 * It will check if you're on a server and get the correct env accordingly.
 * @returns ServerEnvironment
 */
export function getSharedEnvironment(): ServerEnvironment {
  if (isServer) {
    return getServerEnvironment();
  } else {
    return getClientEnvironment();
  }
}
